import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import InfoBanner from '../InfoBanner/InfoBanner';
import en from './locales/facultyExperienceSwitcher.en.json';
import { load, save } from './switcherStorage';
import {
	clientUrl,
	get,
	halFetch,
	I18n,
	logger,
	normalizeLocaleData,
} from '@dm/components';
import { useLocation } from 'react-router';
import getLocationUserId from '../../packages/@dm/navbar/utils/getLocationUserId';
import getLocationInstrumentId from '../../packages/@dm/navigation/utils/getLocationInstrumentId';

const getInstruments = get('data._embedded.activities:instrument');

const messages = normalizeLocaleData(
	en,
	'@dm/app-loader.facultyExperienceSwitcher'
);

function getHome(isNew, instrumentId) {
	if (isNew) {
		return clientUrl.clientAppUrl(`/home/instruments/${instrumentId}`);
	}
	return clientUrl.clientAppUrl(`/activities/instruments/${instrumentId}`);
}

function State({ children, metadata, instruments }) {
	const { httpSessionId, facultyExperienceViewDefault } = metadata;
	const location = useLocation();
	const initialState = useMemo(() => load(httpSessionId), [httpSessionId]);
	const isDefault = facultyExperienceViewDefault === 'true';

	const [isNew, setIsNew] = useState(initialState.isNew);
	const [isVisible, setIsVisible] = useState(initialState.isVisible);

	const instrumentId =
		getLocationInstrumentId(location) || instruments[0].instrumentId;
	useEffect(() => {
		save(httpSessionId, { isNew, isVisible });
		if (isNew !== initialState.isNew) {
			window.location = getHome(isNew, instrumentId);
		}
	}, [isNew, isVisible, httpSessionId, initialState, instrumentId]);

	useEffect(() => {
		if (!isNew && isDefault) {
			setIsNew(true);
			setIsVisible(false);
		}
	}, [isDefault, isNew]);

	return children({
		isNew: initialState.isNew,
		setIsNew,
		isVisible: isVisible || (isNew && !isDefault),
		setIsVisible,
	});
}

function Switcher({ isNew, metadata, onClose, setIsNew }) {
	const { orgContext, orgId } = metadata;
	const toggleIsNew = useCallback(
		(e) => {
			e.preventDefault();
			setIsNew(!isNew);
			logger('Faculty Experience Switcher Toggle', {
				isNew: !isNew,
				orgContext,
				orgId,
				page: '@dm/app-loader',
			});
		},
		[isNew, orgContext, orgId, setIsNew]
	);

	const handleClose = useCallback(() => {
		onClose();
		logger('Faculty Experience Switcher Close', {
			isNew,
			orgContext,
			orgId,
			page: '@dm/app-loader',
		});
	}, [onClose, orgContext, orgId]);

	const [el, setEl] = useState(null);
	useEffect(() => {
		const header = document.querySelector('#header');
		const container = document.createElement('div');

		const handleResize = function () {
			if (container.previousElementSibling !== header) {
				header.after(container);
			}
		};

		handleResize();
		setEl(container);
		// Other sticky sub-headers (Ex. activities summary screen) may steal this
		//  prime spot so we need to reposition when that happens
		window.addEventListener('resize', handleResize);

		const observer = new MutationObserver(handleResize);
		observer.observe(document.body, { subtree: false, childList: true });

		return () => {
			window.removeEventListener('resize', handleResize);
			container.remove();
			observer.disconnect();
		};
	}, []);

	if (!el) {
		return null;
	}

	if (isNew) {
		return (
			<InfoBanner canClose={false}>
				<I18n
					escape={false}
					i18n="@dm/app-loader.facultyExperienceSwitcher.new-message"
				/>{' '}
				<a href="#" onClick={toggleIsNew}>
					<I18n
						escape={false}
						i18n="@dm/app-loader.facultyExperienceSwitcher.new-link"
					/>
				</a>
			</InfoBanner>
		);
	}
	return ReactDOM.createPortal(
		<InfoBanner
			canClose={true}
			onClose={handleClose}
			closeI18n="@dm/app-loader.facultyExperienceSwitcher.close"
		>
			<I18n
				escape={false}
				i18n="@dm/app-loader.facultyExperienceSwitcher.original-message"
			/>{' '}
			<a href="#" onClick={toggleIsNew}>
				<I18n
					escape={false}
					i18n="@dm/app-loader.facultyExperienceSwitcher.original-link"
				/>
			</a>
		</InfoBanner>,
		el
	);
}

function AppSwitcher({ NavbarApp, NavigationApp, metadata }) {
	const location = useLocation();
	const managedUserId = getLocationUserId(location);
	const [instruments, setInstruments] = useState(false);

	useEffect(() => {
		halFetch(metadata.apiURL, {
			rels: ['activities:api', 'activities:instruments'],
			params: { userId: metadata.userId },
		}).then((data) => {
			setInstruments(getInstruments(data));
		});
	}, [metadata]);

	// PRO-12080 hide in manage data view or non-faculty
	const hasInstruments = instruments && instruments.length > 0;
	if (
		!hasInstruments ||
		metadata.facultyExperienceView !== 'true' ||
		metadata.pendoFAUserRole !== 'FA_FACULTY' ||
		managedUserId !== null
	) {
		return <NavbarApp metadata={metadata} />;
	}

	return (
		<State metadata={metadata} instruments={instruments}>
			{({ isNew, setIsNew, isVisible, setIsVisible }) => (
				<>
					{isNew && <NavigationApp metadata={metadata} />}
					{!isNew && <NavbarApp metadata={metadata} />}
					{isVisible && (
						<Switcher
							isNew={isNew}
							metadata={metadata}
							onClose={() => setIsVisible(false)}
							setIsNew={setIsNew}
						/>
					)}
				</>
			)}
		</State>
	);
}

export default function (props) {
	return (
		<IntlProvider
			key="en"
			locale="en"
			messages={messages}
			textComponent={React.Fragment}
		>
			<AppSwitcher {...props} />
		</IntlProvider>
	);
}
